import { Stores, User } from "../types";
import { makeAutoObservable } from "mobx";
import api from "../services/api";
import { useSnackbar } from "notistack";

export class SessionStore {
  stores: Stores;

  isInitialized = false;
  user: User | null = null;
  isSubmitting = false;
  sessionToken: string = "";

  get isLogged() {
    return this.user !== null;
  }

  constructor(stores: Stores) {
    this.stores = stores;
    makeAutoObservable(this);
  }

  initialize = async () => {
    try {
      const user = await api.getCurrentUser();
      if (user) {
        this.restoreSession();
        this.user = user;
      } else {
        this.user = null;
      }
    } catch (err) {
      throw err;
    } finally {
      this.isInitialized = true;
    }
  };

  login = async (email: User["email"], password: string) => {
    this.isSubmitting = true;

    try {
      await api.login(email, password);
      this.initialize();
    } catch (err) {
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  sendRecoveryLink = async (email: User["email"]) => {
    this.isSubmitting = true;
    try {
      await api.lostPassword(email);
    } catch (err) {
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  changePassword = async (
    email: User["email"],
    password: string,
    token: string
  ) => {
    this.isSubmitting = true;

    try {
      await api.changePassword(email, password, token);
    } catch (err) {
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  changeSessionUserPassword = async (newPassword: string) => {
    if (!this.user) {
      return false;
    }

    this.isSubmitting = true;

    try {
      await api.changeSessionUserPassword(this.user.email, newPassword);
      if (!this.user.signupCompleted) {
        this.user.signupCompleted = true;
      }
    } catch (err) {
      throw err;
    } finally {
      this.isSubmitting = false;
    }
  };

  restoreSession = async () => {
    const data = localStorage.getItem("session");
    const sessionData = data ? JSON.parse(data) : null;

    if (sessionData) {
      this.sessionToken = sessionData.token;
    }
  };

  persistSession = async () => {
    const data = {
      token: this.sessionToken,
    };
    localStorage.setItem("session", JSON.stringify(data));
  };

  resetSession = () => {
    this.user = null;
    this.sessionToken = "";
    localStorage.removeItem("session");
  };
}
